import PropTypes from "prop-types";
import { useCallback, useEffect, useState } from "react";
import { Empty, Spin } from "antd";
import { FaCheck } from "react-icons/fa";
import { InfoCircleOutlined } from "@ant-design/icons";
import AxlePolicyDetails from "./AxlePolicyDetails";
import RentalAgreementCard from "./RentalAgreementCard";
import apiClient from "../../../../../helpers/apiClient";
import {
    downloadBonzahPolicyPdfFile,
    parseBlobErrorMessage,
} from "../../../../insurance/lib/insuranceSaleApi";
import { useToast } from "../../../../../contexts/ToastContext";

const InsuranceCoverageCard = ({
    resDetails,
    insurancePolicyData,
    t,
    isInsuranceLoading,
    onRefresh,
    embedded = false,
    hideHeader = false,
    hideRentalAgreement = false,
}) => {
    const toast = useToast();
    const [isDownloadingBonzahCard, setIsDownloadingBonzahCard] = useState(false);
    const [isDownloadingTeqCard, setIsDownloadingTeqCard] = useState(false);
    const [abiCardUrl, setAbiCardUrl] = useState(() => resDetails?.abiInsurance?.cardUrl || null);
    const [isRefreshingAbiCard, setIsRefreshingAbiCard] = useState(false);

    useEffect(() => {
        setAbiCardUrl(resDetails?.abiInsurance?.cardUrl || null);
    }, [resDetails?.abiInsurance?.cardUrl, resDetails?.id]);

    // Function to get Teqmobility payload features (handles single object or array)
    const getTeqmobilityFeatures = () => {
        if (!insurancePolicyData) return [];

        const addons = Array.isArray(insurancePolicyData) ? insurancePolicyData : [insurancePolicyData];
        const addonSections = [];

        addons.forEach((addon) => {
            if (addon?.featureList && Array.isArray(addon.featureList) && addon?.name) {
                const activeFeatures = addon.featureList.filter((feature) => feature.isActive && feature.text);

                if (activeFeatures.length > 0) {
                    addonSections.push({
                        addonName: addon.name,
                        features: activeFeatures.map((feature) => ({
                            text: feature.text,
                            isActive: true,
                        })),
                    });
                 }
            }
        });

        return addonSections;
    };

    // Function to format insurance type
    const getInsuranceTypeDisplay = () => {
        if (!resDetails?.insuranceType) {
            return t("resDetails.noInsuranceSelected");
        }

        const insuranceType = resDetails.insuranceType.toLowerCase();

        if (insuranceType === "teqmobility") {
            return (
                <span>
                    {t("resDetails.providedBy")} <span className="font-bold text-blue-600">Teqmobility</span>
                </span>
            );
        }

        if (insuranceType === "bonzah") {
            return (
                <span>
                    {t("resDetails.providedBy")} <span className="font-bold text-blue-600">Bonzah</span>
                </span>
            );
        }

        if (insuranceType === "abi") {
            return (
                <span>
                    {t("resDetails.providedBy")} <span className="font-bold text-blue-600">INSHUR</span>
                </span>
            );
        }

        const capitalizedType = resDetails.insuranceType.charAt(0).toUpperCase() + resDetails.insuranceType.slice(1);

        // Return JSX with styled capitalizedType
        return (
            <span>
                {t("resDetails.insurance")} {t("resDetails.verifiedBy")}{" "}
                <span style={{ color: "#5D5DFF", fontWeight: "bold" }}>{capitalizedType}</span>
            </span>
        );
    };

    // Check if insurance uses addon structure (Teqmobility, Bonzah - same featureList format)
    const isAddonInsurance = ["teqmobility", "bonzah", "abi"].includes(resDetails?.insuranceType?.toLowerCase());

    // Per-provider status object (pending/active/completed/failed) carried on the reservation —
    // mirrors the booking site's `useReservationInsuranceDetails` provider selection.
    const getAddonStatusInsurance = () => {
        const insuranceType = resDetails?.insuranceType?.toLowerCase();
        if (insuranceType === "bonzah") return resDetails?.bonzahInsurance;
        if (insuranceType === "abi") return resDetails?.abiInsurance;
        if (insuranceType === "teqmobility") return resDetails?.teqMobilityInsurance;
        return null;
    };

    const addonStatusInsurance = getAddonStatusInsurance();
    const insuranceStatusKey = ["pending", "active", "completed", "failed"].includes(
        addonStatusInsurance?.status,
    )
        ? addonStatusInsurance.status
        : null;

    const insuranceStatusConfig = {
        pending: {
            label: t("resDetails.insuranceStatusReadyToStart"),
            dotClass: "bg-[#12B76A]",
            badgeClass: "bg-[#ECFDF3] text-[#027A48]",
        },
        active: {
            label: t("resDetails.insuranceStatusStarted"),
            dotClass: "bg-[#12B76A]",
            badgeClass: "bg-[#ECFDF3] text-[#027A48]",
        },
        completed: {
            label: t("resDetails.insuranceStatusEnded"),
            dotClass: "bg-[#2E90FA]",
            badgeClass: "bg-[#EFF8FF] text-[#175CD3]",
        },
        failed: {
            label: t("resDetails.insuranceStatusFailedToStart"),
            dotClass: "bg-[#F04438]",
            badgeClass: "bg-[#FEF3F2] text-[#B42318]",
        },
    }[insuranceStatusKey];

    // Generalized version of the ABI-only failed message below — surfaces the provider error
    // for any addon insurance (Teqmobility/Bonzah/ABI), matching the booking site's behavior.
    const insuranceErrorMessage =
        addonStatusInsurance?.status === "failed" && addonStatusInsurance?.lastError
            ? addonStatusInsurance.lastError.includes(":")
                ? addonStatusInsurance.lastError.split(":")[1]?.trim() || addonStatusInsurance.lastError
                : addonStatusInsurance.lastError
            : null;

    const getAddonRecords = () => {
        if (!insurancePolicyData) return [];
        return Array.isArray(insurancePolicyData) ? insurancePolicyData : [insurancePolicyData];
    };

    const getBonzahPolicyRecord = () => {
        const list = getAddonRecords();
        const bonzah = list.find((a) => String(a?.provider || "").toLowerCase() === "bonzah");
        return bonzah || list[0];
    };

    const getProviderLinks = () => {
        const records = getAddonRecords();
        const withLinks = records.find((item) => item?.providerLinks && typeof item.providerLinks === "object");
        return withLinks?.providerLinks || null;
    };

    const getTeqmobilityPolicyRecord = () => {
        const list = getAddonRecords();
        const teq = list.find((a) => {
            const p = String(a?.provider || "").toLowerCase().replace(/\s+/g, "");
            return p === "teqmobility" || p === "teqmobilityinsurance";
        });
        return teq || list[0];
    };

    const getTeqmobilityCardUrl = () => {
        const rec = getTeqmobilityPolicyRecord();
        const fromRecord =
            rec?.cardUrl ||
            rec?.insuranceCardUrl ||
            rec?.card_url ||
            rec?.insurance_card_url ||
            rec?.documentUrl;
        if (fromRecord) return fromRecord;
        if (!Array.isArray(insurancePolicyData) && insurancePolicyData) {
            const fromAddonDoc =
                insurancePolicyData.cardUrl ||
                insurancePolicyData.insuranceCardUrl ||
                insurancePolicyData.card_url ||
                insurancePolicyData.documentUrl;
            if (fromAddonDoc) return fromAddonDoc;
        }
        const teqOnRes =
            resDetails?.teqMobilityInsurance ||
            resDetails?.teqmobilityInsurance ||
            resDetails?.teq_mobility_insurance;
        return (
            teqOnRes?.cardUrl ||
            teqOnRes?.card_url ||
            teqOnRes?.insuranceCardUrl ||
            teqOnRes?.documentUrl ||
            null
        );
    };

    const getABICardUrl = () => abiCardUrl || null;

    const isAddonInsuranceCardStatusOk = (status) => {
        if (status === undefined || status === null || status === "") return true;
        const s = String(status).toLowerCase();
        return s === "active" || s === "completed";
    };

    const bonzahPolicyStatus = getBonzahPolicyRecord()?.status;
    const shouldShowBonzahDownload =
        resDetails?.insuranceType?.toLowerCase() === "bonzah" &&
        Boolean(resDetails?.id) &&
        !isInsuranceLoading &&
        Boolean(insurancePolicyData) &&
        isAddonInsuranceCardStatusOk(bonzahPolicyStatus);

    const teqPolicyStatus =
        getTeqmobilityPolicyRecord()?.status ??
        resDetails?.teqMobilityInsurance?.status ??
        resDetails?.teqmobilityInsurance?.status;
    const teqCardUrl = getTeqmobilityCardUrl();
    const shouldShowTeqmobilityCardLink =
        resDetails?.insuranceType?.toLowerCase() === "teqmobility" &&
        Boolean(resDetails?.id) &&
        !isInsuranceLoading &&
        Boolean(teqCardUrl) &&
        isAddonInsuranceCardStatusOk(teqPolicyStatus);

    const abiStatus = resDetails?.abiInsurance?.status;
    const abiCardUrlValue = getABICardUrl();
    const shouldShowABICardLink =
        resDetails?.insuranceType?.toLowerCase() === "abi" &&
        Boolean(abiCardUrlValue) &&
        isAddonInsuranceCardStatusOk(abiStatus);
    const shouldShowABIRefreshCard =
        resDetails?.insuranceType?.toLowerCase() === "abi" &&
        !abiCardUrlValue &&
        Boolean(resDetails?.abiInsurance?.rentalId) &&
        isAddonInsuranceCardStatusOk(abiStatus);

    const openAbiCardUrl = useCallback(
        (cardUrl) => {
            if (!cardUrl) return;
            const link = document.createElement("a");
            link.href = cardUrl;
            link.target = "_blank";
            link.rel = "noopener noreferrer";
            if (cardUrl.startsWith("data:application/pdf;base64,")) {
                link.download = `abi-insurance-card-${resDetails?.id || "card"}.pdf`;
            }
            link.style.display = "none";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        },
        [resDetails?.id],
    );

    const handleViewABICard = useCallback(() => {
        openAbiCardUrl(abiCardUrlValue);
    }, [abiCardUrlValue, openAbiCardUrl]);

    const handleRefreshABICard = useCallback(async () => {
        if (!resDetails?.id || isRefreshingAbiCard) return;

        setIsRefreshingAbiCard(true);
        try {
            const response = await apiClient.get(
                `/api/insurance/abi-period-zero-id-card/${resDetails.id}`,
            );

            const refreshedCardUrl = response.data?.data?.cardUrl;
            if (!response.data?.success || !refreshedCardUrl) {
                throw new Error(t("resDetails.downloadInsuranceCardError"));
            }

            setAbiCardUrl(refreshedCardUrl);
            openAbiCardUrl(refreshedCardUrl);
        } catch (error) {
            toast.error({
                message:
                    error.response?.data?.message ||
                    error.message ||
                    t("resDetails.downloadInsuranceCardError"),
            });
        } finally {
            setIsRefreshingAbiCard(false);
        }
    }, [isRefreshingAbiCard, toast, openAbiCardUrl, resDetails, t]);

    const handleDownloadBonzahCard = useCallback(async () => {
        if (!resDetails?.id) return;

        setIsDownloadingBonzahCard(true);
        try {
            await downloadBonzahPolicyPdfFile(
                resDetails.id,
                `bonzah-insurance-card-${resDetails.id}.pdf`,
            );
        } catch (error) {
            let errText = t("resDetails.downloadInsuranceCardError");
            if (error.response?.data instanceof Blob) {
                const parsed = await parseBlobErrorMessage(error.response.data);
                if (parsed) errText = parsed;
            } else if (typeof error?.message === "string" && error.message && !error.response) {
                errText = error.message;
            }
            toast.error({ message: errText });
        } finally {
            setIsDownloadingBonzahCard(false);
        }
    }, [toast, resDetails?.id, t]);

    const handleDownloadTeqCard = useCallback(async () => {
        if (!resDetails?.id) return;

        setIsDownloadingTeqCard(true);
        try {
            const response = await apiClient.post(
                "/api/insurance/download-teqmobility-pdf",
                { reservationId: resDetails.id },
                { responseType: "blob" },
            );

            const contentType = String(
                response.headers["content-type"] || response.headers["Content-Type"] || "",
            ).toLowerCase();
            const blob = response.data;

            if (contentType.includes("application/json")) {
                const errMsg = await parseBlobErrorMessage(blob);
                throw new Error(errMsg || t("resDetails.downloadInsuranceCardError"));
            }

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `teqmobility-insurance-card-${resDetails.id}.pdf`;
            a.style.display = "none";
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch {
            // If no backend proxy endpoint yet, fall back to opening in new tab
            if (teqCardUrl) {
                window.open(teqCardUrl, "_blank", "noopener,noreferrer");
            }
        } finally {
            setIsDownloadingTeqCard(false);
        }
    }, [resDetails?.id, t, teqCardUrl]);

    return (
        <div className={embedded ? "flex flex-col" : "flex flex-col rounded-2xl bg-white p-5"}>
            {!hideHeader ? (
                <div
                    className="fontbold"
                    style={{
                        fontSize: "1rem",
                        marginBottom: "1rem",
                    }}>
                    {t("resDetails.insuranceTitle")}
                </div>
            ) : null}

            {/* Show loading spinner when insurance data is being fetched */}
            {isInsuranceLoading ? (
                <div
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        padding: "2rem",
                        flex: 1,
                    }}>
                    <Spin size="default" tip={t("resDetails.loadingInsuranceData")} />
                </div>
            ) : !resDetails?.insuranceType ? (
                /* Show "No Insurance Coverage Taken" when no insurance is selected */
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "2rem",
                        flex: 1,
                        textAlign: "center",
                    }}>
                    <div
                        style={{
                            width: "60px",
                            height: "60px",
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            marginBottom: "1rem",
                        }}>
                        <InfoCircleOutlined style={{ fontSize: "48px", color: "#8c8c8c" }} />
                    </div>
                    <p
                        style={{
                            fontSize: "1rem",
                            color: "#8c8c8c",
                            margin: 0,
                            fontWeight: "500",
                        }}>
                        {t("resDetails.noInsuranceCoverageTaken")}
                    </p>
                </div>
            ) : (
                <>
                    {/* Insurance type */}
                    <div className="flex items-start justify-between">
                        <div className="font-medium">{getInsuranceTypeDisplay()}</div>
                    </div>

                    {shouldShowBonzahDownload && (
                        <div style={{ marginTop: "0.5rem" }}>
                            <button
                                type="button"
                                disabled={isDownloadingBonzahCard}
                                onClick={handleDownloadBonzahCard}
                                className="text-sm font-medium text-[#5D5DFF] hover:text-[#4d4dff] underline bg-transparent border-0 p-0 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">
                                {t("resDetails.downloadInsuranceCard")}
                            </button>
                        </div>
                    )}

                    {shouldShowTeqmobilityCardLink && (
                        <div style={{ marginTop: "0.5rem" }}>
                            <button
                                type="button"
                                disabled={isDownloadingTeqCard}
                                onClick={handleDownloadTeqCard}
                                className="text-sm font-medium text-[#5D5DFF] hover:text-[#4d4dff] underline bg-transparent border-0 p-0 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">
                                {t("resDetails.downloadInsuranceCard")}
                            </button>
                        </div>
                    )}

                    {shouldShowABICardLink && (
                        <div style={{ marginTop: "0.5rem" }}>
                            <button
                                type="button"
                                onClick={handleViewABICard}
                                className="text-sm font-medium text-[#5D5DFF] hover:text-[#4d4dff] underline bg-transparent border-0 p-0 cursor-pointer">
                                {t("resDetails.downloadInsuranceCard")}
                            </button>
                        </div>
                    )}

                    {shouldShowABIRefreshCard && (
                        <div style={{ marginTop: "0.5rem" }}>
                            <button
                                type="button"
                                disabled={isRefreshingAbiCard}
                                onClick={handleRefreshABICard}
                                className="text-sm font-medium text-[#5D5DFF] hover:text-[#4d4dff] underline bg-transparent border-0 p-0 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">
                                {t("resDetails.refreshInsuranceCard")}
                            </button>
                        </div>
                    )}

                    {resDetails?.insuranceType?.toLowerCase() === "abi" &&
                        String(resDetails?.abiInsurance?.status || "").toLowerCase() === "failed" && (
                            <div style={{ marginTop: "0.5rem" }}>
                                <p style={{ fontSize: "0.75rem", color: "#dc2626", margin: 0 }}>
                                    {t("resDetails.insuranceStatus")}: {resDetails.abiInsurance.status}
                                </p>
                                {resDetails.abiInsurance.lastError && (
                                    <p style={{ fontSize: "0.75rem", color: "#6B7280", margin: "0.25rem 0 0" }}>
                                        {resDetails.abiInsurance.lastError}
                                    </p>
                                )}
                            </div>
                        )}

                    {/* Insurance activation status — mirrors the booking site's status badge
                        (pending/active/completed/failed → ready to start/started/ended/failed) */}
                    {isAddonInsurance && (insuranceStatusConfig || insuranceErrorMessage) ? (
                        <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
                            {insuranceStatusConfig ? (
                                <span
                                    className={`inline-flex w-fit items-center gap-2 rounded-full px-3 py-1 text-xs font-medium ${insuranceStatusConfig.badgeClass}`}>
                                    <span className={`h-2 w-2 rounded-full ${insuranceStatusConfig.dotClass}`} />
                                    {insuranceStatusConfig.label}
                                </span>
                            ) : null}
                            {insuranceErrorMessage ? (
                                <p className="m-0 inline-flex items-start gap-1.5 text-xs text-[#B42318]">
                                    <InfoCircleOutlined className="mt-0.5 shrink-0" />
                                    <span>{insuranceErrorMessage}</span>
                                </p>
                            ) : null}
                        </div>
                    ) : null}

                    {/* Axle policy details (carrier, dates, insureds, vehicles, coverages, documents) */}
                    {!isAddonInsurance && insurancePolicyData ? (
                        <AxlePolicyDetails resDetails={resDetails} policy={insurancePolicyData} t={t} />
                    ) : null}

                    {/* Coverage list for addon insurance (Teqmobility, Bonzah, ABI) */}
                    {isAddonInsurance && getTeqmobilityFeatures().length > 0 && (
                        <div style={{ marginTop: "0.5rem" }} className="space-y-3">
                            <p
                                className="fontSemi"
                                style={{ fontSize: "0.8rem", color: "#666", marginBottom: "0.5rem" }}>
                                {t("resDetails.coverages")}:
                            </p>

                            {/* Addon insurance with grouped features */}
                            {getTeqmobilityFeatures().map((addonSection, sectionIndex) => (
                                <div key={sectionIndex} style={{ marginBottom: "1rem" }}>
                                    {/* Addon name */}
                                    <p
                                        style={{
                                            fontSize: "0.8rem",
                                            fontWeight: "600",
                                            color: "#374151",
                                            marginBottom: "0.25rem",
                                            textTransform: "capitalize",
                                        }}>
                                        {addonSection.addonName}
                                    </p>
                                    {/* Coverage type */}
                                    {addonSection.coverageType && (
                                        <p
                                            style={{
                                                fontSize: "0.7rem",
                                                color: "#6B7280",
                                                marginBottom: "0.5rem",
                                                marginLeft: "0.75rem",
                                            }}>
                                            {addonSection.coverageType.replace(/_/g, " ")}
                                        </p>
                                    )}

                                    {/* Features for this addon */}
                                    <div style={{ marginLeft: "0.75rem" }}>
                                        {addonSection.features.map((feature, featureIndex) => (
                                            <div
                                                key={featureIndex}
                                                style={{
                                                    display: "flex",
                                                    alignItems: "flex-start",
                                                    gap: "0.5rem",
                                                    marginBottom: "0.25rem",
                                                }}>
                                                <div style={{ flexShrink: 0, marginTop: "0.125rem" }}>
                                                    <FaCheck color="green" size={10} />
                                                </div>
                                                <p
                                                    style={{
                                                        flex: 1,
                                                        fontSize: "0.75rem",
                                                        color: "#666",
                                                        margin: 0,
                                                        lineHeight: "1.2",
                                                        wordBreak: "break-word",
                                                    }}>
                                                    {feature.text}
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Show empty state only when there is truly nothing to show */}
                    {((isAddonInsurance &&
                        getTeqmobilityFeatures().length === 0 &&
                        !shouldShowTeqmobilityCardLink &&
                        !shouldShowABICardLink &&
                        !shouldShowABIRefreshCard) ||
                        (!isAddonInsurance && !insurancePolicyData)) && (
                        <Empty
                            description={t("resDetails.noCoverageDataAvailable")}
                            style={{ paddingTop: "10px", paddingBottom: "10px" }}
                            imageStyle={{ height: 40 }}
                        />
                    )}

                    {/* Provider policy links (excluded vehicles / terms / privacy) — same links
                        surfaced on the booking site's insurance details panel */}
                    {getProviderLinks() ? (
                        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-[#f0f0f0] pt-3 text-xs">
                            {getProviderLinks().excludedVehicles ? (
                                <a
                                    href={getProviderLinks().excludedVehicles}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-[#5D5DFF] hover:underline">
                                    {t("resDetails.providerLinksExcludedVehicles")}
                                </a>
                            ) : null}
                            {getProviderLinks().terms ? (
                                <a
                                    href={getProviderLinks().terms}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-[#5D5DFF] hover:underline">
                                    {t("resDetails.providerLinksTerms")}
                                </a>
                            ) : null}
                            {getProviderLinks().privacy ? (
                                <a
                                    href={getProviderLinks().privacy}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-[#5D5DFF] hover:underline">
                                    {t("resDetails.providerLinksPrivacy")}
                                </a>
                            ) : null}
                        </div>
                    ) : null}
                </>
            )}
            {!hideRentalAgreement ? (
                <RentalAgreementCard resDetails={resDetails} t={t} onRefresh={onRefresh} />
            ) : null}
        </div>
    );
};

InsuranceCoverageCard.propTypes = {
    resDetails: PropTypes.object,
    insurancePolicyData: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    t: PropTypes.func.isRequired,
    isInsuranceLoading: PropTypes.bool,
    onRefresh: PropTypes.func,
    embedded: PropTypes.bool,
    hideHeader: PropTypes.bool,
    hideRentalAgreement: PropTypes.bool,
};

export default InsuranceCoverageCard;
